/**
 * ProfileImageUpload — reusable image upload widget for modals
 *
 * Two operating modes:
 *
 *  EDIT MODE   (userId provided)
 *    - Uploads immediately when the user selects a file
 *    - Calls onUploadSuccess(newAvatarUrl) on success
 *
 *  CREATE MODE (userId === null)
 *    - Previews the file locally without uploading
 *    - Calls onFileSelected(File) so the parent can upload after the entity is created
 *
 * Props:
 *   name             {string}   — displayed in avatar fallback / alt text
 *   userId           {number|null}
 *   shopId           {number}
 *   currentAvatarUrl {string|null}
 *   onUploadSuccess  {function(newUrl)}  — called after a successful upload (edit mode)
 *   onFileSelected   {function(File)}   — called when a file is selected (create mode)
 *   onRemoveSuccess  {function()}        — called after successful remove (edit mode)
 *   size             {string}            — 'md' | 'lg' | 'xl' (default 'lg')
 *   accentColor      {string}            — hex color for buttons
 */
import React, { useRef, useState } from 'react';
import axiosClient from '../../api/axiosClient';
import ProfileAvatar from './ProfileAvatar';

const ACCENT = '#7d6633';
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];
const MAX_MB = parseInt(process.env.REACT_APP_PROFILE_IMAGE_MAX_MB || '5', 10);

const ProfileImageUpload = ({
  name = '',
  userId = null,
  shopId,
  currentAvatarUrl = null,
  onUploadSuccess,
  onFileSelected,
  onRemoveSuccess,
  size = 'lg',
  accentColor = ACCENT,
}) => {
  const fileInputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState('');

  const displayUrl = previewUrl || currentAvatarUrl;

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!e.target.files?.length) return;
    // Reset input so the same file can be re-selected after removal
    e.target.value = '';

    setError('');

    // Validate type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Only PNG and JPEG images are allowed.');
      return;
    }

    // Validate size
    if (file.size > MAX_MB * 1024 * 1024) {
      setError(`Image must be smaller than ${MAX_MB}MB.`);
      return;
    }

    // Show local preview immediately
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    if (userId && shopId) {
      // EDIT MODE — upload immediately
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append('image', file);

        const response = await axiosClient.post(
          `/admin/profile-images/${userId}?shop_id=${shopId}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        if (response.data?.ok) {
          const newUrl = response.data.data.avatar_url;
          setPreviewUrl(null);
          window.dispatchEvent(new CustomEvent('profile-image-updated', {
            detail: { userId, avatar_url: newUrl },
          }));
          onUploadSuccess?.(newUrl);
        } else {
          throw new Error(response.data?.message || 'Upload failed');
        }
      } catch (err) {
        console.error('[ProfileImageUpload] upload error:', err);
        setError(err.response?.data?.message || 'Upload failed. Please try again.');
        setPreviewUrl(null);
      } finally {
        setUploading(false);
      }
    } else {
      // CREATE MODE — just pass the file to the parent
      onFileSelected?.(file);
    }
  };

  const handleRemove = async () => {
    setError('');
    setPreviewUrl(null);

    if (userId && shopId) {
      // EDIT MODE — delete on server
      setRemoving(true);
      try {
        await axiosClient.delete(
          `/admin/profile-images/${userId}?shop_id=${shopId}`
        );
        window.dispatchEvent(new CustomEvent('profile-image-updated', {
          detail: { userId, avatar_url: null },
        }));
        onRemoveSuccess?.();
      } catch (err) {
        console.error('[ProfileImageUpload] remove error:', err);
        setError('Failed to remove image. Please try again.');
      } finally {
        setRemoving(false);
      }
    } else {
      // CREATE MODE — just clear the staged file
      onFileSelected?.(null);
    }
  };

  const hasImage = !!(previewUrl || currentAvatarUrl);
  const isBusy = uploading || removing;

  return (
    <div className="flex flex-col items-center gap-3">
      {/* Avatar preview — image click previews; buttons handle upload/replace */}
      <div className="relative group">
        <ProfileAvatar
          name={name}
          avatarUrl={displayUrl}
          size={size}
          onClick={!hasImage && !isBusy ? () => fileInputRef.current?.click() : undefined}
          showEditHint
          previewable={hasImage && !isBusy}
        />
        {/* Spinner overlay while uploading */}
        {isBusy && (
          <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
            <svg
              className="animate-spin h-5 w-5 text-white"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8z"
              />
            </svg>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex gap-2 flex-wrap justify-center">
        <button
          type="button"
          disabled={isBusy}
          onClick={() => fileInputRef.current?.click()}
          className="px-3 py-1.5 text-xs font-medium rounded-lg border transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background: accentColor,
            color: '#fff',
            borderColor: accentColor,
          }}
        >
          {hasImage ? 'Replace Photo' : 'Upload Photo'}
        </button>

        {hasImage && (
          <button
            type="button"
            disabled={isBusy}
            onClick={handleRemove}
            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Remove Photo
          </button>
        )}
      </div>

      {/* Helper text */}
      <p className="text-xs text-zinc-500 dark:text-zinc-400 text-center">
        PNG or JPEG · max {MAX_MB}MB
      </p>

      {/* Error */}
      {error && (
        <p className="text-xs text-red-600 dark:text-red-400 text-center">{error}</p>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png"
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
};

export default ProfileImageUpload;
